import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { PostHorizontalRule } from '../extensions/PostHorizontalRule.js';
import { PostSpacer } from '../extensions/PostSpacer.js';
import { PathstoneBulletList } from '../extensions/PathstoneBulletList.js';
import { PathstoneOrderedList } from '../extensions/PathstoneOrderedList.js';
import { PathstoneBlockquote } from '../extensions/PathstoneBlockquote.js';
import { PathstoneLink } from '../extensions/PathstoneLink.js';
import { PathstoneListItem } from '../extensions/PathstoneListItem.js';
import { PostIndentExtension } from '../extensions/PostIndentExtension.js';
import { PostImageCarousel } from '../extensions/PostImageCarousel.js';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { createLowlight } from 'lowlight';
import TextAlign from '@tiptap/extension-text-align';
import Mention from '@tiptap/extension-mention';
import { Markdown } from 'tiptap-markdown';
import markdownitMark from 'markdown-it-mark';
import markdownitTaskLists from 'markdown-it-task-lists';
import markdownitFootnote from 'markdown-it-footnote';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { VideoExtension } from '../extensions/VideoExtension.js';
import { EmbedVideoExtension } from '../extensions/EmbedVideoExtension.js';
import { CaptionExtension } from '../extensions/CaptionExtension.js';
import { DownloadExtension, DownloadRowExtension } from '../extensions/DownloadExtension.js';
import AudioExtension from '../extensions/AudioExtension.js';
import { AudioRowExtension } from '../extensions/AudioExtension.js';
import { ExternalLinkExtension, ExternalLinkRowExtension } from '../extensions/ExternalLinkExtension.js';
import { MeetingExtension, MeetingRowExtension } from '../extensions/MeetingExtension.js';
import { ResourceTableExtension } from '../extensions/ResourceTableExtension.js';
import { ResourceRowParagraphBehavior } from '../extensions/ResourceRowParagraphBehavior.js';
import { handlePostEditorBackspace } from '../lib/post_editor_backspace.js';
import { PostListKeymap } from '../lib/post_list_keymap.js';
import { FontFamilyClassExtension } from '../extensions/FontFamilyClassExtension.js';
import { PostParagraphStyleExtension } from '../extensions/PostParagraphStyleExtension.js';
import { PsTextColorExtension } from '../extensions/PsTextColorExtension.js';
import PsHighlightExtension from '../extensions/PsHighlightExtension.js';
import { MentionListComponent } from './MentionListComponent.js';
import { TiptapToolbar } from './TiptapToolbar.jsx';
import { TiptapActiveEditorProvider } from './TiptapActiveEditorContext.jsx';
import { DragContextMenu } from '@/components/tiptap-ui/drag-context-menu'
import { UiState } from '@/components/tiptap-extension/ui-state-extension'
import ImageUploadNode from '@/components/tiptap-node/image-upload-node/image-upload-node-extension'
import FileUploadNode from '@/components/tiptap-node/file-upload-node/file-upload-node-extension'
import '@/components/tiptap-node/image-upload-node/image-upload-node.scss'
import { Image } from '@/components/tiptap-node/image-node/image-node-extension';
import { MediaNodeFloating } from './MediaNodeFloating.jsx';
import { BlockquoteNodeFloating } from './BlockquoteNodeFloating.jsx';
import { resolveAdminTiptapFeatures } from './tiptap_admin_features.js';
import { serializePostEditorHtml } from '../lib/post_content_html.js';
import { POST_EDITOR_DISABLED_STARTER_KIT_MARKS } from '../lib/post_editor_constants.js';
import { mergeBlockIndentAttrs } from '../lib/post_block_indent.js';
import {
  buildPasteSliceFromClipboard,
  normalizeClipboardPlainText,
  pastePlainTextIntoList,
  plainTextFromClipboardEvent,
  trimTrailingEmptyPasteLines,
} from '../lib/post_editor_paste.js';
import { findListItemDepth } from '../lib/resource_row_paragraph.js';

// Function to refresh resource titles in editor
const refreshResourceTitles = async (editor) => {
  if (!editor) return;

  // Find all nodes with resourceId
  const resourceIds = new Set();
  editor.state.doc.descendants((node) => {
    if (node.attrs.resourceId) {
      resourceIds.add(node.attrs.resourceId);
    }
  });

  if (resourceIds.size === 0) return;

  // Fetch resource data in batch
  const csrfToken = document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || '';
  
  try {
    const resources = await Promise.all(
      Array.from(resourceIds).map(async (resourceId) => {
        try {
          const response = await fetch(`/admin/resources/${resourceId}.json`, {
            headers: {
              'Accept': 'application/json',
              'X-CSRF-Token': csrfToken
            }
          });
          if (response.ok) {
            return await response.json();
          }
          return null;
        } catch (error) {
          console.error(`Error fetching resource ${resourceId}:`, error);
          return null;
        }
      })
    );

    // Update editor content with latest resource titles
    const resourceMap = new Map();
    resources.filter(Boolean).forEach(resource => {
      resourceMap.set(resource.id.toString(), resource.title);
    });

    if (resourceMap.size === 0) return;

    // Collect all updates first, then apply in a single transaction
    // Only update downloadRow, externalLinkRow, and audioRow nodes, NOT image/video nodes (they have captions, not titles)
    const updates = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.attrs.resourceId && resourceMap.has(node.attrs.resourceId.toString())) {
        const nodeType = node.type.name;
        if (nodeType !== 'downloadRow' && nodeType !== 'externalLinkRow' && nodeType !== 'audioRow') {
          return; // Skip image, video, and other node types
        }
        
        const latestTitle = resourceMap.get(node.attrs.resourceId.toString());
        const paragraph = node.content.firstChild;
        
        if (paragraph && paragraph.textContent !== latestTitle) {
          const from = pos + 1; // Start of paragraph
          const to = pos + node.nodeSize - 1; // End of paragraph
          updates.push({ from, to, title: latestTitle });
        }
      }
    });

    // Apply all updates in a single transaction
    if (updates.length > 0) {
      const tr = editor.state.tr;
      // Apply updates in reverse order to maintain positions
      updates.reverse().forEach(({ from, to, title }) => {
        tr.replaceWith(from, to, editor.schema.nodes.paragraph.create(
          {},
          editor.schema.text(title)
        ));
      });
      editor.view.dispatch(tr);
    }
  } catch (error) {
    console.error('Error refreshing resource titles:', error);
  }
};

export const TiptapEditorComponent = ({ 
  initialContent = '', 
  placeholder = 'Start writing your post content...',
  onChange,
  onReady,
  className = '',
  toolbarClassName = '',
  editorClassName = '',
  editable = true,
  // Back-compat props (older callers):
  toolbarMode,
  enableUrlPasteHandler,

  // New admin-only config:
  featuresPreset = 'posts',
  features = {}
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const resolvedFeatures = useMemo(() => {
    const resolved = resolveAdminTiptapFeatures({ preset: featuresPreset, override: features });

    // Allow legacy props to override when explicitly provided.
    if (typeof toolbarMode === 'string') resolved.toolbarMode = toolbarMode;
    if (typeof enableUrlPasteHandler === 'boolean') resolved.pasteResourceHandler = enableUrlPasteHandler;

    return resolved;
  }, [featuresPreset, features, toolbarMode, enableUrlPasteHandler]);

  // Initialize editor
  
  // uploadFile matches the ImageUploadNode UploadFunction signature:
  // (file, onProgress?, abortSignal?) => Promise<string>
  const uploadFile = useCallback((file, onProgress, abortSignal) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const url = '/direct_uploads'
      const formData = new FormData()
      formData.append('file', file)

      xhr.open('POST', url)
      const csrf = document.querySelector('meta[name=csrf-token]')?.content
      if (csrf) xhr.setRequestHeader('X-CSRF-Token', csrf)

      xhr.upload.onprogress = function (event) {
        if (event.lengthComputable && typeof onProgress === 'function') {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress({ progress })
        }
      }

      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText)
            const mediaUrl = data.url || `/rails/active_storage/blobs/${data.signed_id}/${encodeURIComponent(data.filename)}`
            resolve(mediaUrl)
          } catch (err) {
            reject(new Error('Invalid JSON response from upload endpoint'))
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`))
        }
      }

      xhr.onerror = function () {
        reject(new Error('Network error during upload'))
      }

      if (abortSignal) {
        if (abortSignal.aborted) {
          xhr.abort()
          reject(new Error('Upload aborted'))
          return
        }
        abortSignal.addEventListener('abort', () => {
          try { xhr.abort() } catch (e) {}
          reject(new Error('Upload aborted'))
        })
      }

      xhr.send(formData)
    })
  }, [])

  // Initialize editor via useEditor so React NodeViews mount correctly
  const lowlight = useMemo(() => createLowlight(), []);
    
  const createdEditor = useEditor({
      editable,
      extensions: [
        StarterKit.configure({
          ...POST_EDITOR_DISABLED_STARTER_KIT_MARKS,
          codeBlock: false,
          horizontalRule: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
          blockquote: false,
          paragraph: false,
          // The post editor exposes only 4 paragraph styles (Body, Caption,
          // Heading, Subheading). PostParagraphStyleExtension renders the
          // last two as semantic <h2>/<h3> directly. We disable StarterKit's
          // Heading node so it doesn't fight with PostParagraphStyleExtension
          // over <h2>/<h3> tag matching at parse time.
          heading: false,
          listKeymap: false,
          link: false,
        }),
        ...(resolvedFeatures.links
          ? [
              PathstoneLink.configure({
                openOnClick: true,
                HTMLAttributes: {
                  class: 'tiptap-link',
                  target: '_blank',
                  rel: 'noopener noreferrer',
                },
              }),
            ]
          : []),
        PostParagraphStyleExtension,
        PathstoneListItem,
        PostListKeymap,
        PathstoneBulletList,
        PathstoneBlockquote,
        PostHorizontalRule,
        PostSpacer,
        PathstoneOrderedList,
        PostIndentExtension,
        ...(resolvedFeatures.media ? [PostImageCarousel] : []),
        FontFamilyClassExtension,
        PsTextColorExtension,
        PsHighlightExtension,
        Subscript,
        ...(resolvedFeatures.superscript ? [Superscript] : []),
        ...(resolvedFeatures.textAlign
          ? [
              TextAlign.configure({
                // 'heading' removed from the list — there is no longer a
                // separate `heading` node type in this editor; semantic
                // <h2>/<h3> are rendered by PostParagraphStyleExtension
                // (node name still 'paragraph').
                types: ['paragraph', 'tableCell', 'tableHeader'],
              }),
            ]
          : []),
        ...(resolvedFeatures.tables
          ? [Table.configure({ resizable: true }), TableRow, TableCell, TableHeader]
          : []),

        ...(resolvedFeatures.media
          ? [
              CaptionExtension.configure({
                types: ['image', 'video', 'embedVideo'],
                placeholder: 'Enter caption...',
              }),
              Image.configure({ resize: false }),
              ImageUploadNode.configure({ upload: uploadFile, limit: 1, accept: 'image/*,image/svg+xml,video/*' }),
              VideoExtension,
              EmbedVideoExtension,
            ]
          : []),

        ...(resolvedFeatures.files
          ? [FileUploadNode.configure({ upload: uploadFile, limit: 1, accept: '*/*' })]
          : []),

        ...(resolvedFeatures.codeBlocks ? [CodeBlockLowlight.configure({ lowlight })] : []),

        ...(resolvedFeatures.resources
          ? [
              DownloadExtension,
              DownloadRowExtension,
              ExternalLinkExtension,
              ExternalLinkRowExtension,
              MeetingExtension,
              MeetingRowExtension,
              AudioExtension,
              AudioRowExtension,
              ResourceTableExtension,
              ResourceRowParagraphBehavior,
            ]
          : []),

        ...(resolvedFeatures.media || resolvedFeatures.files || resolvedFeatures.resources ? [UiState] : []),

        ...(resolvedFeatures.tasks ? [TaskList, TaskItem] : []),

        ...(resolvedFeatures.mentions
          ? [
              Mention.configure({
                HTMLAttributes: {
                  class: 'mention',
                },
                suggestion: {
                  items: async ({ query }) => {
                    try {
                      const response = await fetch(`/mentions?q=${encodeURIComponent(query)}`);
                      if (!response.ok) {
                        console.error('Failed to fetch users');
                        return [];
                      }
                      const users = await response.json();
                      return users.slice(0, 10);
                    } catch (error) {
                      console.error('Error fetching users:', error);
                      return [];
                    }
                  },
                  render: () => {
                    let component;
                    let popup;

                    return {
                      onStart: (props) => {
                        component = new MentionListComponent(props);
                        popup = document.createElement('div');
                        popup.className = 'mention-suggestions';
                        document.body.appendChild(popup);
                        popup.style.position = 'fixed';
                        popup.style.zIndex = '9999';
                        component.render(popup);
                      },
                      onUpdate(props) {
                        component.updateProps(props);
                      },
                      onKeyDown(props) {
                        if (props.event.key === 'Escape') {
                          popup?.remove();
                          return true;
                        }
                        return component.onKeyDown(props);
                      },
                      onExit() {
                        popup?.remove();
                      },
                    };
                  },
                },
              }),
            ]
          : []),

        ...(resolvedFeatures.markdown
          ? [
              Markdown.configure({
                html: true,
                linkify: !!resolvedFeatures.links,
                // We paste as plain text via editorProps transforms; letting the Markdown extension
                // transform pasted text collapses newlines (Markdown treats single newlines as spaces).
                transformPastedText: false,
                transformCopiedText: true,
                bulletListMarker: '-',
                markdownitPlugins: [markdownitMark, markdownitTaskLists, markdownitFootnote],
              }),
            ]
          : []),
      ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      onChange?.(serializePostEditorHtml(editor.getHTML()));
    },
    onCreate: ({ editor }) => {
      // Refresh resource titles on editor creation
      refreshResourceTitles(editor);
      window.currentTiptapEditor = editor;
      onChange?.(serializePostEditorHtml(editor.getHTML()));
      onReady?.(editor);
    },
      editorProps: {
        attributes: {
          class: `tiptap-editor ${editorClassName}`,
          placeholder,
        },
        // Runs before extension keymaps so list joinBackward cannot beat resource/quote unwrap/merge.
        handleKeyDown: handlePostEditorBackspace,
        // Paste: plain text only (ui-refresh) — newlines → paragraphs, no formatting.
        transformPastedHTML: () => '',
        transformPastedText: (text) => normalizeClipboardPlainText(text),
        handlePaste: (view, event) => {
          const text = plainTextFromClipboardEvent(event);
          const lines = trimTrailingEmptyPasteLines(
            normalizeClipboardPlainText(text).split('\n')
          );
          if (!lines.length) return false;

          const editor = view.dom?.editor ?? window.currentTiptapEditor;
          const listItemDepth = findListItemDepth(view.state.selection.$from);

          if (listItemDepth >= 0 && lines.length > 1 && editor) {
            event.preventDefault();
            pastePlainTextIntoList(editor, lines);
            return true;
          }

          const slice = buildPasteSliceFromClipboard(view.state.schema, event);
          if (!slice) return false;

          event.preventDefault();
          view.dispatch(view.state.tr.replaceSelection(slice));
          return true;
        },
      },
  });

  // Toolbar upload handler (used by legacy toolbar buttons).
  // This is separate from node-based uploads, but keeps the toolbar functional.
  const handleFileUpload = useCallback(async (file) => {
    if (!createdEditor || !file) return;

    setIsLoading(true);
    try {
      const result = await uploadToAzure(file);
      const mediaUrl = result?.url;

      if (!mediaUrl) throw new Error('Upload completed but no URL returned');

      if (file.type === 'image/svg+xml') {
        const reader = new FileReader();
        reader.onload = function (e) {
          const svgDataUrl = e.target.result;
          createdEditor.chain().focus().setImage({ src: svgDataUrl }).run();
        };
        reader.readAsDataURL(file);
        return;
      }

      if (file.type.startsWith('image/')) {
        createdEditor.chain().focus().setImage({ src: mediaUrl }).run();
      } else if (file.type.startsWith('video/')) {
        createdEditor.chain().focus().insertContent({
          type: 'video',
          attrs: mergeBlockIndentAttrs(createdEditor.state, {
            src: mediaUrl,
            controls: true,
            style: 'max-width:100%',
          }),
        }).run();
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + (error.message || error));
    } finally {
      setIsLoading(false);
    }
  }, [createdEditor]);


  // Cleanup effect
  useEffect(() => {
    return () => {
      if (createdEditor) {
        try { 
          createdEditor.destroy?.();
        } catch (e) {
          console.error('Error destroying editor:', e);
        }
      }
      if (window.currentTiptapEditor) {
        window.currentTiptapEditor = null;
      }
    };
  }, [createdEditor]);

  return (
    <div className={`tiptap-editor-container ${className}`}>
      <TiptapActiveEditorProvider mainEditor={createdEditor}>
        {createdEditor && (
          <TiptapToolbar
            editor={createdEditor}
            isLoading={isLoading}
            className={toolbarClassName}
            featuresPreset={featuresPreset}
            toolbarMode={resolvedFeatures.toolbarMode}
            enableUrlPasteHandler={!!resolvedFeatures.pasteResourceHandler}
            features={{
              resources: !!resolvedFeatures.resources,
              media: !!resolvedFeatures.media,
              files: !!resolvedFeatures.files,
              pasteResourceHandler: !!resolvedFeatures.pasteResourceHandler,
              pasteVideoEmbeds: !!resolvedFeatures.pasteVideoEmbeds,
              superscript: !!resolvedFeatures.superscript,
            }}
          />
        )}
        {createdEditor && <BlockquoteNodeFloating editor={createdEditor} />}
        {createdEditor && (resolvedFeatures.media || resolvedFeatures.files || resolvedFeatures.resources) && (
          <>
            <DragContextMenu editor={createdEditor} withSlashCommandTrigger={false} />
            {(resolvedFeatures.media || resolvedFeatures.files) && <MediaNodeFloating editor={createdEditor} />}
          </>
        )}
        <EditorContent editor={createdEditor} />
      </TiptapActiveEditorProvider>

      {isLoading && (
        <div className="tiptap-upload-loader">
          <div className="tiptap-loader-spinner" />
        </div>
      )}
    </div>
  );
};
